import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { cloneDeep } from "lodash";
import TongHopDatLichService from "services/TongHopDatLichService";

export const searchTongHop = createAsyncThunk(
  "tongHopDatLich/search",
  async (data, { rejectWithValue }) => {
    try {
      const response = await TongHopDatLichService.search(data);
      return response.data;
    } catch (err) {
      return rejectWithValue(err.message || "Error");
    }
  }
);

export const createTongHop = createAsyncThunk(
  "tongHopDatLich/create",
  async (data, { rejectWithValue }) => {
    try {
      const { onSuccess } = data;
      const payload = cloneDeep(data);
      delete payload.onSuccess;
      const response = await TongHopDatLichService.create(payload);
      if (onSuccess) onSuccess(response.data);
      return response.data;
    } catch (err) {
      return rejectWithValue(err.message || "Error");
    }
  }
);

export const getTongHopById = createAsyncThunk(
  "tongHopDatLich/getById",
  async (id, { rejectWithValue }) => {
    try {
      const response = await TongHopDatLichService.getById(id);
      return response.data;
    } catch (err) {
      return rejectWithValue(err.message || "Error");
    }
  }
);

export const updateTongHop = createAsyncThunk(
  "tongHopDatLich/update",
  async (data, { rejectWithValue }) => {
    try {
      const { onSuccess } = data;
      const payload = cloneDeep(data);
      delete payload.onSuccess;
      const response = await TongHopDatLichService.update(payload);
      if (onSuccess) onSuccess(response.data);
      return response.data;
    } catch (err) {
      return rejectWithValue(err.message || "Error");
    }
  }
);

export const deleteTongHop = createAsyncThunk(
  "tongHopDatLich/delete",
  async (data, { rejectWithValue }) => {
    try {
      const { onSuccess, id } = data;
      const response = await TongHopDatLichService.delete(id);
      if (onSuccess) onSuccess(response.data);
      return response.data;
    } catch (err) {
      return rejectWithValue(err.message || "Error");
    }
  }
);

export const chotTongHop = createAsyncThunk(
  "tongHopDatLich/chot",
  async (data, { rejectWithValue }) => {
    try {
      const { onSuccess, id } = data;
      const response = await TongHopDatLichService.chot(id);
      if (onSuccess) onSuccess(response.data);
      return response.data;
    } catch (err) {
      return rejectWithValue(err.message || "Error");
    }
  }
);

export const getChiTietList = createAsyncThunk(
  "tongHopDatLich/getChiTiet",
  async (tongHopId, { rejectWithValue }) => {
    try {
      const response = await TongHopDatLichService.getChiTiet(tongHopId);
      return response.data;
    } catch (err) {
      return rejectWithValue(err.message || "Error");
    }
  }
);

export const getAllChiTietList = createAsyncThunk(
  "tongHopDatLich/getAllChiTiet",
  async (ids, { rejectWithValue }) => {
    try {
      const responses = await Promise.all(
        (ids || []).map((id) => TongHopDatLichService.getChiTiet(id))
      );
      return responses.flatMap((r) => r.data || []);
    } catch (err) {
      return rejectWithValue(err.message || "Error");
    }
  }
);

export const updateChiTiet = createAsyncThunk(
  "tongHopDatLich/updateChiTiet",
  async (data, { rejectWithValue }) => {
    try {
      const { onSuccess } = data;
      const payload = cloneDeep(data);
      delete payload.onSuccess;
      const response = await TongHopDatLichService.updateChiTiet(payload);
      if (onSuccess) onSuccess(response.data);
      return response.data;
    } catch (err) {
      return rejectWithValue(err.message || "Error");
    }
  }
);

const initialState = {
  loading: false,
  tongHopList: [],
  tongHopDetail: null,
  chiTietList: [],
};

const tongHopDatLichSlice = createSlice({
  name: "tongHopDatLich",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(searchTongHop.pending, (state) => { state.loading = true; })
      .addCase(searchTongHop.fulfilled, (state, action) => {
        state.loading = false;
        state.tongHopList = action.payload || [];
      })
      .addCase(searchTongHop.rejected, (state) => { state.loading = false; })

      .addCase(createTongHop.pending, (state) => { state.loading = true; })
      .addCase(createTongHop.fulfilled, (state) => { state.loading = false; })
      .addCase(createTongHop.rejected, (state) => { state.loading = false; })

      .addCase(getTongHopById.pending, (state) => { state.loading = true; })
      .addCase(getTongHopById.fulfilled, (state, action) => {
        state.loading = false;
        state.tongHopDetail = action.payload;
      })
      .addCase(getTongHopById.rejected, (state) => { state.loading = false; })

      .addCase(updateTongHop.pending, (state) => { state.loading = true; })
      .addCase(updateTongHop.fulfilled, (state) => { state.loading = false; })
      .addCase(updateTongHop.rejected, (state) => { state.loading = false; })

      .addCase(deleteTongHop.pending, (state) => { state.loading = true; })
      .addCase(deleteTongHop.fulfilled, (state) => { state.loading = false; })
      .addCase(deleteTongHop.rejected, (state) => { state.loading = false; })

      .addCase(chotTongHop.pending, (state) => { state.loading = true; })
      .addCase(chotTongHop.fulfilled, (state) => { state.loading = false; })
      .addCase(chotTongHop.rejected, (state) => { state.loading = false; })

      .addCase(getChiTietList.pending, (state) => { state.loading = true; })
      .addCase(getChiTietList.fulfilled, (state, action) => {
        state.loading = false;
        state.chiTietList = action.payload || [];
      })
      .addCase(getChiTietList.rejected, (state) => { state.loading = false; })

      .addCase(getAllChiTietList.pending, (state) => { state.loading = true; })
      .addCase(getAllChiTietList.fulfilled, (state, action) => {
        state.loading = false;
        state.chiTietList = action.payload || [];
      })
      .addCase(getAllChiTietList.rejected, (state) => { state.loading = false; })

      .addCase(updateChiTiet.pending, (state) => { state.loading = true; })
      .addCase(updateChiTiet.fulfilled, (state) => { state.loading = false; })
      .addCase(updateChiTiet.rejected, (state) => { state.loading = false; });
  },
});

export default tongHopDatLichSlice.reducer;
